import { useState, useMemo } from "react";
import { Users, Layers } from "lucide-react";
import { useProductionPerformance } from "../hooks/useProductionPerformance";
import { useProductionBaseline } from "../hooks/useProductionBaseline";
import {
  toBangkokTimestamp,
  toBangkokTimestampEndOfDay,
} from "@/shared/lib/commission-date-utils";
import { t } from "@/shared/lib/th-translations";
import type { PerformanceMetric } from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import PerformanceSmallMultiples from "../components/production/PerformanceSmallMultiples";
import PerformanceHeatmap from "../components/production/PerformanceHeatmap";
import PerformanceDotPlot from "../components/production/PerformanceDotPlot";
import PerformanceRankedList from "../components/production/PerformanceRankedList";

type WindowDays = 1 | 7 | 14 | 30;
type VizMode = "heatmap" | "dotPlot" | "rankedList" | "smallMultiples";

const HIDDEN_PROCESS_TITLES = new Set(["พิมพ์เอกสาร", "แพค"]);

function computeWindow(days: WindowDays): { fromMs: number; toMs: number } {
  const today = new Date();
  const toMs = toBangkokTimestampEndOfDay(today);
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - (days - 1));
  const fromMs = toBangkokTimestamp(startDate);
  return { fromMs, toMs };
}

export default function ProductionPerformance() {
  const [windowDays, setWindowDays] = useState<WindowDays>(1);
  const [metric, setMetric] = useState<PerformanceMetric>("shots");
  const [viz, setViz] = useState<VizMode>("heatmap");
  const [machineSplit, setMachineSplit] = useState(false);

  const { fromMs, toMs } = useMemo(() => computeWindow(windowDays), [windowDays]);
  const { data, isLoading, error } = useProductionPerformance({ fromMs, toMs });
  const { data: baselineResp } = useProductionBaseline(30);

  const processes = useMemo(() => {
    const raw = data?.data?.processes ?? [];
    return raw.filter((p) => !HIDDEN_PROCESS_TITLES.has(p.processTitle));
  }, [data]);

  const totalProcesses = processes.length;
  const totalEmployees = useMemo(() => {
    const ids = new Set<string>();
    for (const p of processes) for (const b of p.buckets) ids.add(b.employeeId);
    return ids.size;
  }, [processes]);

  const windowOptions: { label: string; value: WindowDays }[] = [
    { label: t("production.performance.window.today"), value: 1 },
    { label: t("production.performance.window.last7Days"), value: 7 },
    { label: t("production.performance.window.last14Days"), value: 14 },
    { label: t("production.performance.window.last30Days"), value: 30 },
  ];

  const metricOptions: { label: string; value: PerformanceMetric }[] = [
    { label: t("production.performance.metric.goodUnits"), value: "good_units" },
    { label: t("production.performance.metric.events"), value: "events" },
    { label: t("production.performance.metric.shots"), value: "shots" },
    { label: t("production.performance.metric.paperLength"), value: "paper_length" },
    { label: t("production.performance.metric.paperArea"), value: "paper_area" },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">
          {t("navigation.productionPerformance")}
        </h1>
        <p className="text-muted-foreground">
          {t("production.performance.subtitle")}
        </p>
      </div>

      {/* Caveat */}
      <Alert>
        <AlertDescription className="text-sm text-muted-foreground">
          {t("production.performance.caveat")}
        </AlertDescription>
      </Alert>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.performance.window.label")}:
          </span>
          <div className="flex gap-1">
            {windowOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={windowDays === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setWindowDays(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.performance.metric.label")}:
          </span>
          <div className="flex gap-1">
            {metricOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={metric === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setMetric(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.performance.stats.totalProcesses")}
            </CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalProcesses.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.performance.stats.totalEmployees")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalEmployees.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {t("production.performance.error")}
          </AlertDescription>
        </Alert>
      )}

      {/* Viz tabs */}
      {isLoading ? (
        <Skeleton className="h-[400px] w-full rounded-lg" />
      ) : processes.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          {t("production.performance.empty")}
        </div>
      ) : (
        <Tabs value={viz} onValueChange={(v) => setViz(v as VizMode)}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="heatmap">
              {t("production.performance.viz.tabs.heatmap")}
            </TabsTrigger>
            <TabsTrigger value="dotPlot">
              {t("production.performance.viz.tabs.dotPlot")}
            </TabsTrigger>
            <TabsTrigger value="rankedList">
              {t("production.performance.viz.tabs.rankedList")}
            </TabsTrigger>
            <TabsTrigger value="smallMultiples">
              {t("production.performance.viz.tabs.smallMultiples")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="heatmap" className="mt-4">
            <PerformanceHeatmap
              processes={processes}
              metric={metric}
              baseline={baselineResp?.data}
              machineSplit={machineSplit}
              onMachineSplitChange={setMachineSplit}
            />
          </TabsContent>
          <TabsContent value="dotPlot" className="mt-4">
            <PerformanceDotPlot processes={processes} metric={metric} />
          </TabsContent>
          <TabsContent value="rankedList" className="mt-4">
            <PerformanceRankedList processes={processes} metric={metric} />
          </TabsContent>
          <TabsContent value="smallMultiples" className="mt-4">
            <PerformanceSmallMultiples processes={processes} metric={metric} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
